import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '../ui/Icons.tsx';
import { ChatSession, UsageStats, Project, ProjectTab } from '../../types';

interface SidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
  onNewChat: () => void;
  onOpenIntegrations: () => void;
  onOpenSchedules: () => void;
  onOpenSettings: () => void;
  onOpenPricing: () => void;
  currentView?: string;
  sessions: ChatSession[];
  currentSessionId: string | null;
  onLoadSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  onRenameSession: (id: string, newTitle: string) => void;
  onClearHistory: () => void;
  usageStats?: UsageStats | null;
  userName?: string;
  userPlan?: string;
  avatar?: string;
  // ─── Project Props ────────────────────────────────────────
  projects?: Project[];
  activeProject?: Project | null;
  activeProjectTab?: ProjectTab;
  onCreateProject?: () => void;
  onSelectProject?: (project: Project) => void;
  onBackToProjects?: () => void;
  onProjectTabChange?: (tab: ProjectTab) => void;
  projectSessions?: ChatSession[];
  onOpenProjects?: () => void;
}



const getInitials = (name: string) => {
  if (!name) return "G";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
};

export const ProjectIcon = ({ name, className = "w-4 h-4" }: { name: string; className?: string }) => {
  const cleanName = name?.trim() || '';
  
  switch (cleanName) {
    case 'folder':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/></svg>;
    case 'dollar':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 6v12"/></svg>;
    case 'laptop':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="18" height="12" x="3" y="4" rx="2" ry="2"/><line x1="2" x2="22" y1="20" y2="20"/><line x1="12" x2="12" y1="16" y2="20"/></svg>;
    case 'graduation':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.91a2 2 0 0 0 1.66 0z"/><path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"/></svg>;
    case 'brush':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m12 22 1-1c1.4-1.4 2.4-3.2 3-5.2L18 9l-5-5-6.8 2.2c-2 0.6-3.8 1.6-5.2 3l-1 1c-0.6 0.6-0.6 1.6 0 2.2l4.8 4.8c0.6 0.6 1.6 0.6 2.2 0Z"/><path d="M15 6 9 12"/></svg>;
    case 'pen':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>;
    case 'brackets':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M8 3H7a2 2 0 0 0-2 2v3a2 2 0 0 1-2 2 2 2 0 0 1 2 2v3a2 2 0 0 0 2 2h1"/><path d="M16 3h1a2 2 0 0 1 2 2v3a2 2 0 0 0 2 2 2 2 0 0 0-2 2v3a2 2 0 0 1-2 2h-1"/></svg>;
    case 'terminal':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>;
    case 'music':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>;
    case 'gift':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="18" height="14" x="3" y="8" rx="2"/><path d="M12 5a3 3 0 1 0-3 3h6a3 3 0 1 0-3-3Z"/><path d="M12 8v14"/><path d="M3 12h18"/></svg>;
    case 'activity':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
    case 'palette':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 14.7255 3.09032 17.1962 4.85857 19C5.34735 19.4888 6.13636 19.3496 6.3688 18.7058C6.68603 17.8276 7.42603 17 8.5 17H10.5C11.3284 17 12 17.6716 12 18.5V22Z"/><circle cx="7.5" cy="10.5" r="1.5"/><circle cx="11.5" cy="7.5" r="1.5"/><circle cx="16.5" cy="9.5" r="1.5"/></svg>;
    case 'heart':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>;
    case 'sparks':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275Z"/></svg>;
    case 'globe':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"/><line x1="2" x2="22" y1="12" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>;
    case 'briefcase':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>;
    case 'user':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
    case 'dumbbell':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m6.5 6.5 11 11"/><path d="m21 21-1-1"/><path d="m3 3 1 1"/><path d="m18.5 2.5 3 3L20 7l-3-3 1.5-1.5Z"/><path d="m2.5 18.5 3 3L7 20l-3-3-1.5 1.5Z"/><path d="M14 5s.5 1.5 2 3l-3 3s-1.5-.5-3-2l4-4Z"/><path d="M10 19s-.5-1.5-2-3l3-3s1.5.5 3 2l-4 4Z"/></svg>;
    case 'file':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>;
    case 'scale':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="12" y1="2" x2="12" y2="22"/><line x1="5" y1="7" x2="19" y2="7"/><path d="M19 7v6a7 7 0 0 1-14 0V7"/></svg>;
    case 'compass':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>;
    case 'plane':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3.5s-2.5 0-4 1.5L13.5 8.5 5.3 6.7c-.9-.2-1.6.1-2 .7l-.7.7 7.9 3.5-3.5 3.5-3.7-.5H2.1l-.8.8 3.5 1.8 1.8 3.5.8-.8v-1.2l-.5-3.7 3.5-3.5 3.5 7.9.7-.7c.6-.4.9-1.1.7-2Z"/></svg>;
    case 'wrench':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>;
    case 'paw':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="11" cy="4" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="20" cy="15" r="2"/><circle cx="8" cy="19" r="4"/><circle cx="5" cy="14" r="2"/></svg>;
    case 'flask':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M10 2v7.31L3.39 19A1 1 0 0 0 4 20.5h16a1 1 0 0 0 .61-1.5L14 9.32V2Z"/><line x1="8" y1="2" x2="16" y2="2"/><line x1="7.66" y1="12" x2="16.34" y2="12"/></svg>;
    case 'coffee':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M17 8h1a4 4 0 1 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" y1="2" x2="6" y2="4"/><line x1="10" y1="2" x2="10" y2="4"/><line x1="14" y1="2" x2="14" y2="4"/></svg>;
    case 'bag':
      return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>;
    default:
      return <span className="text-base leading-none select-none">{cleanName}</span>;
  }
};

// ─── Project Nav Items ──────────────────────────────────────
const PROJECT_NAV_ITEMS: { id: ProjectTab; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview', icon: '📋' },
  { id: 'plan', label: 'Plan', icon: '🎯' },
  { id: 'activity', label: 'Activity', icon: '⚡' },
  { id: 'timeline', label: 'Timeline', icon: '📊' },
  { id: 'knowledge', label: 'Knowledge', icon: '🧠' },
  { id: 'files', label: 'Files', icon: '📁' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
];

const Sidebar: React.FC<SidebarProps> = ({ 
  isExpanded, 
  onToggle, 
  onNewChat, 
  onOpenIntegrations,
  onOpenSchedules,
  onOpenSettings,
  onOpenPricing,
  currentView,
  sessions, 
  currentSessionId, 
  onLoadSession,
  onDeleteSession,
  onRenameSession,
  onClearHistory,
  usageStats,
  userName = 'Guest',
  userPlan = 'free',
  avatar,
  projects = [],
  activeProject = null,
  activeProjectTab = 'overview',
  onCreateProject,
  onSelectProject,
  onBackToProjects,
  onProjectTabChange,
  projectSessions = [],
  onOpenProjects,
}) => {
  const [isHoveringLogo, setIsHoveringLogo] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProjects, setShowProjects] = useState(false);
  const [activeDropdownSessionId, setActiveDropdownSessionId] = useState<string | null>(null);
  const [deletingSession, setDeletingSession] = useState<ChatSession | null>(null);
  const [pinnedSessionIds, setPinnedSessionIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('neura_pinned_chats');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('neura_pinned_chats', JSON.stringify(pinnedSessionIds));
  }, [pinnedSessionIds]);

  useEffect(() => {
    if (activeProject) {
      setShowProjects(true);
    }
  }, [activeProject]);

  const groupSessionsByDate = (sessionsToGroup: ChatSession[]) => {
    const today: ChatSession[] = [];
    const yesterday: ChatSession[] = [];
    const previousDays: ChatSession[] = [];

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const yesterdayStart = todayStart - 24 * 60 * 60 * 1000;

    sessionsToGroup.forEach(session => {
      if (!session.createdAt) return;
      const sessionTime = new Date(session.createdAt).getTime();
      if (sessionTime >= todayStart) {
        today.push(session);
      } else if (sessionTime >= yesterdayStart) {
        yesterday.push(session);
      } else {
        previousDays.push(session);
      }
    });

    return { today, yesterday, previousDays };
  };

  const sortedSessions = [...sessions].sort((a, b) => {
    const aPinned = pinnedSessionIds.includes(a.id);
    const bPinned = pinnedSessionIds.includes(b.id);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  const filteredSessions = sortedSessions.filter(session =>
    session.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const grouped = groupSessionsByDate(filteredSessions);

  useEffect(() => {
    setIsHoveringLogo(false);
  }, [isExpanded]);

  const handleStartRename = (e: React.MouseEvent, session: ChatSession) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setEditTitle(session.title);
  };

  const handleFinishRename = (id: string) => {
    if (editTitle.trim()) {
      onRenameSession(id, editTitle.trim());
    }
    setEditingSessionId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') handleFinishRename(id);
    if (e.key === 'Escape') setEditingSessionId(null);
  };

  // ─── Custom Icons matching the screenshot ───
  const NewChatIcon = ({ isActive }: { isActive?: boolean }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 transition-colors ${isActive ? 'text-zinc-950 dark:text-white' : 'text-zinc-500 group-hover:text-zinc-905 dark:text-zinc-400 dark:group-hover:text-white'}`}>
      <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"/>
    </svg>
  );

  const SearchIcon = ({ isActive }: { isActive?: boolean }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 transition-colors ${isActive ? 'text-zinc-950 dark:text-white' : 'text-zinc-500 group-hover:text-zinc-905 dark:text-zinc-400 dark:group-hover:text-white'}`}>
      <circle cx="11" cy="11" r="8"/>
      <path d="m21 21-4.3-4.3"/>
    </svg>
  );

  const ProjectsIcon = ({ isActive }: { isActive?: boolean }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 transition-colors ${isActive ? 'text-zinc-950 dark:text-white' : 'text-zinc-500 group-hover:text-zinc-905 dark:text-zinc-400 dark:group-hover:text-white'}`}>
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>
    </svg>
  );

  const ScheduledIcon = ({ isActive }: { isActive?: boolean }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 transition-colors ${isActive ? 'text-zinc-950 dark:text-white' : 'text-zinc-500 group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-white'}`}>
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 14 14"/>
    </svg>
  );

  const AppsIcon = ({ isActive }: { isActive?: boolean }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 transition-colors ${isActive ? 'text-zinc-950 dark:text-white' : 'text-zinc-500 group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-white'}`}>
      <rect width="7" height="7" x="3" y="3" rx="1"/>
      <rect width="7" height="7" x="14" y="3" rx="1"/>
      <rect width="7" height="7" x="14" y="14" rx="1"/>
      <rect width="7" height="7" x="3" y="14" rx="1"/>
    </svg>
  );

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // PROJECT MODE — When a project is selected
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const renderProjectMode = () => (
    <>
      {/* Project Header with Back Button */}
      <div className={`flex flex-col w-full mb-4 ${isExpanded ? '' : 'items-center'}`}>
        {isExpanded ? (
          <div className="flex items-center gap-2.5 w-full pl-2 pr-0.5">
            <button
              onClick={onBackToProjects}
              className="text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900 flex-shrink-0"
              title="Back to all projects"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div 
              className="w-7 h-7 rounded-lg flex items-center justify-center text-base flex-shrink-0 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white"
            >
              <ProjectIcon name={activeProject!.icon} className="w-4 h-4" />
            </div>
            <span className="text-sm font-semibold text-zinc-900 dark:text-white truncate flex-1 font-sans">
              {activeProject!.name}
            </span>
            <button 
              onClick={onToggle}
              className="text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900"
              title="Collapse Sidebar"
            >
              <Icons.SidebarToggle className="w-[18px] h-[18px]" />
            </button>
          </div>
        ) : (
          <button 
            onClick={onToggle}
            onMouseEnter={() => setIsHoveringLogo(true)}
            onMouseLeave={() => setIsHoveringLogo(false)}
            className="text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900 flex items-center justify-center w-9 h-9 hidden md:flex"
            title="Expand Sidebar"
          >
            {isHoveringLogo ? (
              <Icons.SidebarToggle className="w-[18px] h-[18px] rotate-180 text-zinc-900 dark:text-white" />
            ) : (
              <ProjectIcon name={activeProject!.icon} className="w-4 h-4 text-zinc-900 dark:text-white" />
            )}
          </button>
        )}
      </div>

      {/* Project Navigation (Flat Items) */}
      <div className={`flex flex-col w-full gap-0.5 mb-4 ${isExpanded ? '' : 'items-center'}`}>
        {PROJECT_NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            onClick={() => onProjectTabChange?.(item.id)}
            className={`
              text-sm transition-all rounded-lg flex items-center font-sans group
              ${isExpanded ? 'w-full py-2 px-3 font-medium gap-2.5' : 'justify-center w-9 h-9'}
              ${activeProjectTab === item.id
                ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-950 dark:text-white font-semibold'
                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100/50 dark:hover:bg-zinc-900/60'
              }
            `}
            title={item.label}
          >
            <span className="text-base flex-shrink-0 w-5 text-center">{item.icon}</span>
            {isExpanded && <span>{item.label}</span>}
          </button>
        ))}
      </div>
    </>
  );

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // WORKSPACE MODE — Default sidebar with project list + global nav
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const renderWorkspaceMode = () => {
    const isNewChatActive = (currentView === 'welcome' || currentView === 'chat') && currentSessionId === null && activeProject === null;
    const isScheduledActive = currentView === 'schedules';
    const isAppsActive = currentView === 'integrations';
    const isProjectsActive = currentView === 'projects';

    return (
      <>
        {/* Top Section: Logo & Toggle */}
        <div className={`flex flex-col w-full mb-4 ${isExpanded ? '' : 'items-center'}`}>
          {isExpanded ? (
            <div className="flex items-center justify-between w-full pl-2 pr-0.5">
              <div className="flex items-center gap-2.5">
                <span className="text-xl font-bold tracking-tight text-zinc-900 dark:text-white leading-none pl-1 font-sans">Neura</span>
              </div>
              <button 
                onClick={onToggle}
                className="text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900"
                title="Collapse Sidebar"
              >
                <Icons.SidebarToggle className="w-[18px] h-[18px]" />
              </button>
            </div>
          ) : (
            <button 
              onClick={onToggle}
              onMouseEnter={() => setIsHoveringLogo(true)}
              onMouseLeave={() => setIsHoveringLogo(false)}
              className="text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white transition-colors p-1.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-900 flex items-center justify-center w-9 h-9 hidden md:flex mb-2"
              title="Expand Sidebar"
            >
              {isHoveringLogo ? (
                <Icons.SidebarToggle className="w-[18px] h-[18px] rotate-180" />
              ) : (
                <Icons.Logo className="w-5 h-5 rounded-lg object-contain" />
              )}
            </button>
          )}
        </div>

        {/* Navigation List */}
        <div className={`flex flex-col w-full gap-1 mb-4 ${isExpanded ? '' : 'items-center'}`}>
          {/* New Chat */}
          <button 
            onClick={onNewChat}
            className={`
              transition-all rounded-lg flex items-center group font-sans
              ${isExpanded ? 'w-full py-2.5 px-3 text-sm font-semibold gap-3' : 'justify-center w-9 h-9'}
              ${isNewChatActive 
                ? 'bg-zinc-100 text-zinc-950 dark:bg-zinc-900 dark:text-white font-bold' 
                : 'text-zinc-700 dark:text-zinc-300 hover:text-zinc-955 dark:hover:text-white hover:bg-zinc-100/80 dark:hover:bg-zinc-900/60'}
            `}
            title="New chat"
          >
            <NewChatIcon isActive={isNewChatActive} />
            {isExpanded && <span>New chat</span>}
          </button>

          {/* Search Chats */}
          <button 
            onClick={() => {
              if (!isExpanded) {
                onToggle();
                setShowSearch(true);
              } else {
                setShowSearch(!showSearch);
              }
            }}
            className={`
              text-zinc-700 dark:text-zinc-300 hover:text-zinc-955 dark:hover:text-white hover:bg-zinc-100/80 dark:hover:bg-zinc-900/60 transition-all rounded-lg flex items-center group font-sans
              ${isExpanded ? 'w-full py-2.5 px-3 text-sm font-semibold gap-3' : 'justify-center w-9 h-9'}
              ${showSearch ? 'bg-zinc-100 text-zinc-955 dark:bg-zinc-900 dark:text-white' : ''}
            `}
            title="Search chats"
          >
            <SearchIcon isActive={showSearch} />
            {isExpanded && <span>Search chats</span>}
          </button>



          {/* Projects */}
          <button 
            onClick={() => {
              if (onOpenProjects) {
                onOpenProjects();
              }
              if (isExpanded) {
                setShowProjects(!showProjects);
              } else {
                onToggle();
                setShowProjects(true);
              }
            }}
            className={`
              transition-all rounded-lg flex items-center group font-sans
              ${isExpanded ? 'w-full py-2.5 px-3 text-sm font-semibold justify-between' : 'justify-center w-9 h-9'}
              ${isProjectsActive 
                ? 'bg-zinc-100 text-zinc-955 dark:bg-zinc-900 dark:text-white font-bold' 
                : 'text-zinc-700 dark:text-zinc-300 hover:text-zinc-955 dark:hover:text-white hover:bg-zinc-100/80 dark:hover:bg-zinc-900/60'}
            `}
            title="Projects"
          >
            {isExpanded ? (
              <>
                <div className="flex items-center gap-3">
                  <ProjectsIcon isActive={isProjectsActive} />
                  <span>Projects</span>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`text-zinc-500 transition-transform ${showProjects ? 'rotate-180' : ''}`}>
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </>
            ) : (
              <ProjectsIcon isActive={isProjectsActive} />
            )}
          </button>

          {/* Projects List Submenu */}
          {showProjects && isExpanded && (
            <div className="flex flex-col gap-0.5 ml-4 pl-3 border-l border-zinc-200 dark:border-zinc-800 w-full pr-3 animate-in slide-in-from-top-1 duration-200 mt-1">
              {projects.length > 0 && projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => onSelectProject?.(project)}
                  className={`w-full flex items-center gap-2 py-1.5 px-2 text-xs text-left truncate font-sans transition-all rounded-lg ${
                    activeProject?.id === project.id
                      ? 'bg-zinc-150 text-zinc-950 dark:bg-zinc-900 dark:text-white font-bold'
                      : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-950 dark:hover:text-white hover:bg-zinc-100/50 dark:hover:bg-zinc-900/60 font-medium'
                  }`}
                >
                  <span className="text-xs mr-1 text-zinc-700 dark:text-zinc-350">
                    <ProjectIcon name={project.icon} className="w-3.5 h-3.5" />
                  </span>
                  <span className="truncate">{project.name}</span>
                </button>
              ))}
              <button
                onClick={onCreateProject}
                className="w-full flex items-center gap-2 py-1.5 px-2 text-xs text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-all rounded-lg mt-0.5 font-medium font-sans"
              >
                <span>+</span>
                <span>New Project</span>
              </button>
            </div>
          )}

          {/* Scheduled */}
          <button 
            onClick={onOpenSchedules}
            className={`
              transition-all rounded-lg flex items-center group font-sans
              ${isExpanded ? 'w-full py-2.5 px-3 text-sm font-semibold gap-3' : 'justify-center w-9 h-9'}
              ${isScheduledActive 
                ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-950 dark:text-white font-bold' 
                : 'text-zinc-700 dark:text-zinc-300 hover:text-zinc-955 dark:hover:text-white hover:bg-zinc-100/80 dark:hover:bg-zinc-900/60'}
            `}
            title="Scheduled"
          >
            <ScheduledIcon isActive={isScheduledActive} />
            {isExpanded && <span>Scheduled</span>}
          </button>

          {/* Apps */}
          <button 
            onClick={onOpenIntegrations}
            className={`
              transition-all rounded-lg flex items-center group font-sans
              ${isExpanded ? 'w-full py-2.5 px-3 text-sm font-semibold gap-3' : 'justify-center w-9 h-9'}
              ${isAppsActive 
                ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-955 dark:text-white font-bold' 
                : 'text-zinc-700 dark:text-zinc-300 hover:text-zinc-955 dark:hover:text-white hover:bg-zinc-100/80 dark:hover:bg-zinc-900/60'}
            `}
            title="Apps"
          >
            <AppsIcon isActive={isAppsActive} />
            {isExpanded && <span>Apps</span>}
          </button>
        </div>

        {/* Scrollable Content Area - Recents (History) */}
        <div className={`flex-1 flex flex-col w-full gap-2 overflow-y-auto no-scrollbar ${isExpanded ? 'items-start' : 'items-center'}`}>
          <div className={`flex flex-col w-full ${isExpanded ? '' : 'items-center mt-2'}`}>
            <div className={`flex items-center text-zinc-500 dark:text-zinc-400 mb-2 ${isExpanded ? 'justify-between w-full px-3' : 'justify-center w-9 h-9 rounded-lg cursor-default transition-colors'}`}>
              <div className="flex items-center gap-2">
                {isExpanded && <span className="font-bold text-xs text-zinc-500 dark:text-zinc-400 tracking-wide font-sans">Recents</span>}
              </div>
            </div>
            
            {/* History Items - Only visible when expanded */}
            {isExpanded && (
              <div className="flex flex-col gap-0.5 w-full pr-2">
                {filteredSessions.length === 0 ? (
                  <span className="text-[11px] text-zinc-500 dark:text-zinc-600 px-3 py-1.5 italic">No recent chats</span>
                ) : (
                  filteredSessions.map((session) => (
                    <div 
                      key={session.id} 
                      className={`
                        group relative flex items-center justify-between rounded-lg transition-all w-full
                        ${currentSessionId === session.id 
                          ? 'bg-zinc-100 dark:bg-zinc-900 text-zinc-950 dark:text-white font-medium animate-in' 
                          : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-950 dark:hover:text-white hover:bg-zinc-100/50 dark:hover:bg-zinc-900/40'}
                      `}
                    >
                      {editingSessionId === session.id ? (
                        <input
                          autoFocus
                          className="bg-white border-zinc-200 text-zinc-900 dark:bg-[#050505] dark:text-white dark:border-zinc-700 text-xs px-2.5 py-1.5 rounded-lg w-full outline-none border shadow-sm"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          onBlur={() => handleFinishRename(session.id)}
                          onKeyDown={(e) => handleKeyDown(e, session.id)}
                        />
                      ) : (
                        <>
                          <button 
                            onClick={() => onLoadSession(session.id)}
                            className="flex-1 text-left px-3 py-2 text-xs truncate animate-in flex items-center gap-1.5"
                          >
                            {pinnedSessionIds.includes(session.id) && (
                              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5 text-zinc-400 shrink-0 rotate-45">
                                <line x1="12" y1="17" x2="12" y2="22" />
                                <path d="M5 17h14v-1.76a2 2 0 0 0-.44-1.24l-2.78-3.47A2 2 0 0 1 15 9.3V5a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4.3a2 2 0 0 1-.78 1.23l-2.78 3.5A2 2 0 0 0 5 15.24Z" fill="currentColor" />
                              </svg>
                            )}
                            <span className="truncate">{session.title}</span>
                          </button>
                          
                          {/* Actions Dropdown */}
                          <div className="relative shrink-0 pr-2">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdownSessionId(activeDropdownSessionId === session.id ? null : session.id);
                              }}
                              className={`
                                p-1 rounded hover:bg-zinc-200/50 dark:hover:bg-zinc-800 transition-colors text-zinc-400 hover:text-zinc-900 dark:hover:text-white cursor-pointer
                                ${activeDropdownSessionId === session.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                              `}
                              title="Chat actions"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                                <circle cx="12" cy="12" r="1.5" />
                                <circle cx="19" cy="12" r="1.5" />
                                <circle cx="5" cy="12" r="1.5" />
                              </svg>
                            </button>

                            {activeDropdownSessionId === session.id && (
                              <>
                                <div 
                                  className="fixed inset-0 z-40 bg-transparent cursor-default" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdownSessionId(null);
                                  }} 
                                />
                                <div 
                                  className="absolute right-0 mt-1 z-50 bg-white bg-opacity-100 dark:bg-opacity-100 dark:bg-[#121212] border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl py-1 min-w-[150px] flex flex-col animate-in fade-in zoom-in-95 duration-100 font-sans"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={(e) => {
                                      setActiveDropdownSessionId(null);
                                      handleStartRename(e, session);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm font-sans font-normal text-zinc-700 dark:text-zinc-350 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-black dark:hover:text-white transition-all flex items-center gap-2.5 cursor-pointer"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-zinc-400 shrink-0">
                                      <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                      <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"/>
                                    </svg>
                                    Rename
                                  </button>

                                  <button
                                    onClick={() => {
                                      setActiveDropdownSessionId(null);
                                      try {
                                        const shareUrl = window.location.origin + "/chat/" + session.id;
                                        navigator.clipboard.writeText(shareUrl);
                                        alert("Link copied to clipboard!");
                                      } catch (err) {
                                        alert("Failed to copy link.");
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm font-sans font-normal text-zinc-700 dark:text-zinc-350 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-black dark:hover:text-white transition-all flex items-center gap-2.5 cursor-pointer"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-zinc-400 shrink-0">
                                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                      <polyline points="16 6 12 2 8 6" />
                                      <line x1="12" y1="2" x2="12" y2="15" />
                                    </svg>
                                    Share
                                  </button>

                                  <button
                                    onClick={() => {
                                      setActiveDropdownSessionId(null);
                                      const isPinned = pinnedSessionIds.includes(session.id);
                                      if (isPinned) {
                                        setPinnedSessionIds(pinnedSessionIds.filter(id => id !== session.id));
                                      } else {
                                        setPinnedSessionIds([...pinnedSessionIds, session.id]);
                                      }
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm font-sans font-normal text-zinc-700 dark:text-zinc-350 hover:bg-zinc-50 dark:hover:bg-zinc-900 hover:text-black dark:hover:text-white transition-all flex items-center gap-2.5 cursor-pointer"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`w-4 h-4 shrink-0 rotate-45 ${pinnedSessionIds.includes(session.id) ? 'text-zinc-755 dark:text-white fill-zinc-755 dark:fill-white' : 'text-zinc-400'}`}>
                                      <line x1="12" y1="17" x2="12" y2="22" />
                                      <path d="M5 17h14v-1.76a2 2 0 0 0-.44-1.24l-2.78-3.47A2 2 0 0 1 15 9.3V5a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4.3a2 2 0 0 1-.78 1.23l-2.78 3.5A2 2 0 0 0 5 15.24Z" />
                                    </svg>
                                    {pinnedSessionIds.includes(session.id) ? 'Unpin chat' : 'Pin chat'}
                                  </button>

                                  <div className="h-px bg-zinc-100 dark:bg-zinc-800 my-1 w-full" />

                                  <button
                                    onClick={() => {
                                      setActiveDropdownSessionId(null);
                                      setDeletingSession(session);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm font-sans font-normal text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all flex items-center gap-2.5 cursor-pointer"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-red-500 shrink-0">
                                      <path d="M3 6h18"/>
                                      <path d="M19 6v14c0 1-1 2-2 2H7c-1-0-2-1-2-2V6"/>
                                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                                    </svg>
                                    Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div 
      className={`
        h-screen bg-zinc-50 dark:bg-black flex flex-col py-5 flex-shrink-0 z-50 fixed left-0 top-0 border-r border-zinc-200 dark:border-zinc-900 transition-all duration-300 font-sans
        ${isExpanded 
            ? 'translate-x-0 w-64 px-4 shadow-none' 
            : '-translate-x-full w-64 px-4 md:translate-x-0 md:w-16 md:px-0 md:items-center'
        }
      `}
    >
      
      {/* Mode-specific content */}
      {renderWorkspaceMode()}

      {/* Bottom Section — Always visible in both modes */}
      <div className={`mt-auto flex flex-col gap-1 pt-4 border-t border-zinc-200 dark:border-zinc-900 w-full ${isExpanded ? 'items-start' : 'items-center'}`}>
        
        {/* Profile / Settings Navigation Row */}
        {isExpanded ? (
          <button 
            onClick={onOpenSettings}
            className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-900 border border-zinc-200/50 dark:border-zinc-800/80 transition-all text-left min-w-0 group font-sans"
            title="Settings"
          >
            <div className="w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center font-semibold text-xs flex-shrink-0 relative overflow-hidden shadow-inner font-sans">
              {avatar ? (
                <img src={avatar} className="w-full h-full object-cover" alt="Profile" />
              ) : (
                getInitials(userName)
              )}
            </div>
            <div className="min-w-0 flex-1 font-sans">
              <p className="text-sm font-bold text-zinc-900 dark:text-white truncate leading-none">{userName}</p>
              <p className="text-xs text-zinc-400 dark:text-zinc-500 truncate capitalize leading-none mt-1.5">{userPlan} Plan</p>
            </div>
            {/* Settings gear icon always visible */}
            <div className="flex-shrink-0 text-zinc-400 dark:text-zinc-500 pl-1 hover:text-zinc-900 dark:hover:text-white transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </div>
          </button>
        ) : (
          <button 
            onClick={onOpenSettings}
            className="w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center font-medium text-xs hover:scale-105 active:scale-95 transition-all relative overflow-hidden shadow-sm shrink-0 font-sans"
            title="Settings"
          >
            {avatar ? (
              <img src={avatar} className="w-full h-full object-cover" alt="Profile" />
            ) : (
              getInitials(userName)
            )}
          </button>
        )}
      </div>

      {showSearch && createPortal(
        <div 
          className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[999] flex items-center justify-center animate-in fade-in"
          onClick={() => {
            setShowSearch(false);
            setSearchQuery('');
          }}
        >
          <div 
            className="bg-white dark:bg-black w-full max-w-lg mx-4 rounded-2xl border border-zinc-200 dark:border-zinc-900 shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Input Bar */}
            <div className="relative flex items-center border-b border-zinc-200 dark:border-zinc-900">
              <span className="absolute left-4 text-zinc-400 dark:text-zinc-500 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </span>
              <input
                autoFocus
                type="text"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-12 py-4 bg-transparent text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-600 text-sm font-semibold outline-none font-sans"
              />
              <button 
                onClick={() => {
                  setShowSearch(false);
                  setSearchQuery('');
                }}
                className="absolute right-4 text-zinc-400 hover:text-zinc-650 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Modal Body / Results */}
            <div className="px-6 py-4 flex flex-col gap-4 max-h-[350px] overflow-y-auto no-scrollbar font-sans">
              
              {/* New chat action */}
              <button
                onClick={() => {
                  onNewChat();
                  setShowSearch(false);
                  setSearchQuery('');
                }}
                className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-white transition-all text-left"
              >
                <span className="text-zinc-500">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z" />
                  </svg>
                </span>
                <span className="text-xs font-semibold">New chat</span>
              </button>

              {/* Chat groups */}
              {filteredSessions.length === 0 ? (
                <div className="text-center py-6 text-zinc-400 dark:text-zinc-650 text-xs italic">
                  No matching chats found
                </div>
              ) : (
                <>
                  {/* Today */}
                  {grouped.today.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold text-zinc-400 dark:text-zinc-550 uppercase tracking-wider pl-2 py-1">Today</span>
                      {grouped.today.map(session => (
                        <button
                          key={session.id}
                          onClick={() => {
                            onLoadSession(session.id);
                            setShowSearch(false);
                            setSearchQuery('');
                          }}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-white transition-all text-left"
                        >
                          <span className="text-zinc-400 dark:text-zinc-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          </span>
                          <span className="text-xs font-semibold truncate">{session.title}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Yesterday */}
                  {grouped.yesterday.length > 0 && (
                    <div className="flex flex-col gap-1 mt-1">
                      <span className="text-[10px] font-bold text-zinc-400 dark:text-zinc-550 uppercase tracking-wider pl-2 py-1">Yesterday</span>
                      {grouped.yesterday.map(session => (
                        <button
                          key={session.id}
                          onClick={() => {
                            onLoadSession(session.id);
                            setShowSearch(false);
                            setSearchQuery('');
                          }}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:text-zinc-955 dark:hover:text-white transition-all text-left"
                        >
                          <span className="text-zinc-400 dark:text-zinc-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          </span>
                          <span className="text-xs font-semibold truncate">{session.title}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Previous Days */}
                  {grouped.previousDays.length > 0 && (
                    <div className="flex flex-col gap-1 mt-1">
                      <span className="text-[10px] font-bold text-zinc-400 dark:text-zinc-550 uppercase tracking-wider pl-2 py-1">Previous / Days</span>
                      {grouped.previousDays.map(session => (
                        <button
                          key={session.id}
                          onClick={() => {
                            onLoadSession(session.id);
                            setShowSearch(false);
                            setSearchQuery('');
                          }}
                          className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:text-zinc-955 dark:hover:text-white transition-all text-left"
                        >
                          <span className="text-zinc-400 dark:text-zinc-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          </span>
                          <span className="text-xs font-semibold truncate">{session.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </div>
      , document.body)}

      {deletingSession && createPortal(
        <div 
          className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[9999] flex items-center justify-center animate-in fade-in duration-200"
          onClick={() => setDeletingSession(null)}
        >
          <div 
            className="bg-white dark:bg-[#121212] w-full max-w-sm mx-4 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-2xl p-6 flex flex-col gap-4 animate-in zoom-in-95 duration-200 font-sans"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-zinc-950 dark:text-white">Delete Chat</h3>
            
            <p className="text-zinc-650 dark:text-zinc-400 text-sm leading-normal">
              Are you sure you want to delete <strong className="text-zinc-950 dark:text-white">"{deletingSession.title}"</strong>? This action cannot be undone.
            </p>

            <div className="flex items-center gap-3 mt-2">
              <button 
                onClick={() => setDeletingSession(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-800 text-zinc-800 dark:text-zinc-250 text-xs font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button 
                onClick={() => {
                  onDeleteSession(deletingSession.id);
                  setDeletingSession(null);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Sidebar;
